import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "../../lib/api";
import { getAttributionPayload } from "../../lib/analytics";
import { toast } from "sonner";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { Clock3, Sparkles, Leaf, ShieldCheck } from "lucide-react";
import "react-phone-number-input/style.css";
import "./phone-input.css";
import BookingSummaryChips from "./enquiry/BookingSummaryChips";

const hasPrefill = (checkIn, checkOut) => Boolean(checkIn && checkOut);

/* Primary markets first in the phone-country dropdown (audit quick-win). */
const COUNTRY_PRIORITY = ["IN", "AE", "US", "GB", "SG", "AU", "CA", "|", "..."];

export default function EnquiryForm({
    compact = false,
    variant = "light",
    defaultCountry = "IN",
    initialDestination = "",
    initialCheckIn = "",
    initialCheckOut = "",
    initialTravelDates = "",
    initialAdults = 0,
    initialChildren = 0,
    initialInfants = 0,
    initialQuoteTotal = null,
    initialQuoteDeposit = null,
    initialQuoteBreakdown = "",
    initialQuoteAddons = [],
    initialQuoteCurrency = "INR",
    initialQuoteTotalConverted = null,
    initialQuoteDepositConverted = null,
}) {
    const { t } = useTranslation();
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        travel_dates: initialTravelDates,
        check_in: initialCheckIn,
        check_out: initialCheckOut,
        destination_interest: initialDestination,
        adults: initialAdults || 0,
        children: initialChildren || 0,
        infants: initialInfants || 0,
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    // 2-step micro-quiz: step 1 = low-commitment trip intent, step 2 = contact details.
    const twoStep = !hasPrefill(initialCheckIn, initialCheckOut);
    const [step, setStep] = useState(1);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
    const onPhoneChange = (value) => setForm({ ...form, phone: value || "" });

    // Hoisted out of JSX so PhoneInput doesn't see a fresh object every render.
    const phoneNumberInputProps = useMemo(
        () => ({
            required: true,
            "data-testid": "enquiry-phone",
            className: "phone-input-inner",
        }),
        [],
    );

    const hasBookingPrefill = Boolean(initialCheckIn && initialCheckOut);
    const totalGuests = (form.adults || 0) + (form.children || 0) + (form.infants || 0);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.phone || !isValidPhoneNumber(form.phone)) {
            toast.error(t("enquiry.phone_invalid"));
            return;
        }
        setSubmitting(true);
        try {
            const attribution = getAttributionPayload();
            const payload = {
                ...form,
                adults: form.adults ? parseInt(form.adults, 10) : null,
                children: form.children ? parseInt(form.children, 10) : null,
                infants: form.infants ? parseInt(form.infants, 10) : null,
                party_size: totalGuests || null,
                quote_total: initialQuoteTotal || null,
                quote_deposit: initialQuoteDeposit || null,
                quote_breakdown: initialQuoteBreakdown || "",
                quote_addons: initialQuoteAddons || [],
                quote_currency: initialQuoteCurrency || "INR",
                quote_total_converted: initialQuoteTotalConverted || null,
                quote_deposit_converted: initialQuoteDepositConverted || null,
                source_path: typeof window !== "undefined" ? window.location.pathname : "",
                session_id: attribution.session_id || "",
                utm_source: attribution.utm_source || "",
                utm_medium: attribution.utm_medium || "",
                utm_campaign: attribution.utm_campaign || "",
                referrer: attribution.referrer || "",
                partner_code: attribution.partner_code || "",
            };
            await api.post("/enquiries", payload);
            setDone(true);
            setStep(1);
            toast.success(t("enquiry.received_toast"));
            setForm({
                full_name: "",
                email: "",
                phone: "",
                travel_dates: "",
                check_in: "",
                check_out: "",
                destination_interest: "",
                adults: 0,
                children: 0,
                infants: 0,
                message: "",
            });
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || t("enquiry.could_not_send"));
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (!done) return;
        const t = setTimeout(() => setDone(false), 6000);
        return () => clearTimeout(t);
    }, [done]);

    const inputBase =
        variant === "dark"
            ? "w-full bg-transparent border-b border-primary-foreground/30 text-primary-foreground placeholder:text-primary-foreground/50 py-3 px-1 focus:outline-none focus:border-accent transition-colors"
            : "w-full bg-transparent border-b border-foreground/20 text-foreground placeholder:text-muted-foreground py-3 px-1 focus:outline-none focus:border-primary transition-colors";

    return (
        <form onSubmit={onSubmit} className="space-y-6" data-testid="enquiry-form">
            {hasBookingPrefill && (
                <BookingSummaryChips
                    variant={variant}
                    travelDates={form.travel_dates}
                    adults={form.adults}
                    children={form.children}
                    infants={form.infants}
                    quoteTotal={initialQuoteTotal}
                    quoteDeposit={initialQuoteDeposit}
                    quoteCurrency={initialQuoteCurrency}
                    quoteTotalConverted={initialQuoteTotalConverted}
                    quoteDepositConverted={initialQuoteDepositConverted}
                />
            )}

            {twoStep && (
                <div className="flex items-center gap-3" data-testid="enquiry-step-indicator">
                    {[1, 2].map((s) => (
                        <span key={s} className={`text-[10px] tracking-[0.25em] uppercase font-semibold ${step === s ? "text-accent" : variant === "dark" ? "text-primary-foreground/40" : "text-muted-foreground/50"}`}>
                            {s === 1 ? "1 · Your journey" : "2 · Your dossier"}
                        </span>
                    ))}
                    <span className={`flex-1 h-px ${variant === "dark" ? "bg-primary-foreground/20" : "bg-border"}`} />
                </div>
            )}

            {(!twoStep || step === 1) && (
                <div className={`grid ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"} gap-x-8 gap-y-6`}>
                    <input
                        name="destination_interest"
                        placeholder={t("enquiry.destination_interest")}
                        value={form.destination_interest}
                        onChange={onChange}
                        className={inputBase}
                        data-testid="enquiry-destination"
                    />
                    <input
                        name="travel_dates"
                        placeholder={t("enquiry.travel_dates")}
                        value={form.travel_dates}
                        onChange={onChange}
                        className={inputBase}
                        data-testid="enquiry-dates"
                    />
                    {hasBookingPrefill ? (
                        <input
                            type="number"
                            min="0"
                            name="adults"
                            placeholder={t("booking.adults")}
                            value={form.adults || ""}
                            onChange={onChange}
                            className={inputBase}
                            data-testid="enquiry-adults"
                        />
                    ) : (
                        <input
                            type="number"
                            min="1"
                            name="adults"
                            placeholder={t("enquiry.party_size")}
                            value={form.adults || ""}
                            onChange={onChange}
                            className={inputBase}
                            data-testid="enquiry-party"
                        />
                    )}
                </div>
            )}

            {(!twoStep || step === 2) && (
                <>
                    {twoStep && (form.destination_interest || form.travel_dates) && (
                        <p className={`text-xs italic ${variant === "dark" ? "text-primary-foreground/60" : "text-muted-foreground"}`} data-testid="enquiry-step2-recap">
                            {[form.destination_interest, form.travel_dates].filter(Boolean).join(" · ")} — where should our concierge send your custom dossier?
                        </p>
                    )}
                    <div className={`grid ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"} gap-x-8 gap-y-6`}>
                        <input
                            required
                            name="full_name"
                            placeholder={t("enquiry.full_name")}
                            value={form.full_name}
                            onChange={onChange}
                            className={inputBase}
                            data-testid="enquiry-name"
                        />
                        <input
                            required
                            type="email"
                            name="email"
                            placeholder={t("enquiry.email")}
                            value={form.email}
                            onChange={onChange}
                            className={inputBase}
                            data-testid="enquiry-email"
                        />
                        <div data-testid="enquiry-phone-wrapper" className={variant === "dark" ? "phone-input-dark" : "phone-input-light"}>
                            <PhoneInput
                                international
                                defaultCountry={defaultCountry || "IN"}
                                countryOptionsOrder={COUNTRY_PRIORITY}
                                countryCallingCodeEditable={false}
                                placeholder={t("enquiry.phone")}
                                value={form.phone}
                                onChange={onPhoneChange}
                                numberInputProps={phoneNumberInputProps}
                            />
                        </div>
                    </div>
                    <textarea
                        required
                        name="message"
                        rows={4}
                        placeholder={t("enquiry.message_placeholder")}
                        value={form.message}
                        onChange={onChange}
                        className={`${inputBase} resize-none`}
                        data-testid="enquiry-message"
                    />
                </>
            )}

            <div className="flex flex-wrap gap-x-7 gap-y-2 pt-2" data-testid="enquiry-trust-signals">
                {[
                    [ShieldCheck, "100% confidential"],
                    [Clock3, "Replies within 1 working day"],
                    [Sparkles, "100% tailor-made"],
                    [Leaf, "Audited net-zero travel"],
                ].map(([Icon, label]) => (
                    <span
                        key={label}
                        className={`inline-flex items-center gap-2 text-[11px] tracking-wide ${
                            variant === "dark" ? "text-primary-foreground/70" : "text-muted-foreground"
                        }`}
                    >
                        <Icon size={13} strokeWidth={1.5} className="text-accent" /> {label}
                    </span>
                ))}
            </div>
            <div className="pt-4 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                {twoStep && step === 2 ? (
                    <button
                        type="button"
                        data-testid="enquiry-back"
                        onClick={() => setStep(1)}
                        className={`text-xs tracking-[0.2em] uppercase font-semibold text-left ${variant === "dark" ? "text-primary-foreground/60 hover:text-accent" : "text-muted-foreground hover:text-primary"} transition-colors`}
                    >
                        ← Back to journey details
                    </button>
                ) : (
                    <p className={`text-xs ${variant === "dark" ? "text-primary-foreground/55" : "text-muted-foreground"}`}>
                        {t("enquiry.confidence")}
                    </p>
                )}
                {twoStep && step === 1 ? (
                    <button
                        type="button"
                        data-testid="enquiry-next"
                        onClick={() => setStep(2)}
                        className={`inline-flex items-center justify-center px-10 py-4 text-xs tracking-[0.25em] uppercase font-semibold transition-colors duration-300 ${
                            variant === "dark"
                                ? "bg-accent text-accent-foreground hover:bg-accent/85"
                                : "bg-primary text-primary-foreground hover:bg-accent"
                        }`}
                    >
                        Next — request my custom dossier →
                    </button>
                ) : (
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="enquiry-submit"
                        className={`inline-flex items-center justify-center px-10 py-4 text-xs tracking-[0.25em] uppercase font-semibold transition-colors duration-300 disabled:opacity-60 ${
                            variant === "dark"
                                ? "bg-accent text-accent-foreground hover:bg-accent/85"
                                : "bg-primary text-primary-foreground hover:bg-accent"
                        }`}
                    >
                        {submitting ? t("enquiry.sending") : done ? t("enquiry.sent") : t("enquiry.submit")}
                    </button>
                )}
            </div>
        </form>
    );
}
